import React, { useState } from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Checkbox, Group, Text } from '@mantine/core'

import avatars from '#assets/images/avatars.png'
import cursors from '#assets/images/cursors.png'
import blockmenu from '#assets/images/el-menu.png'
import ruler from '#assets/images/ruler.png'
import { ModalShell, useModalControls } from '#components/Modals'
import { useMst } from '#state'

import styles from './ViewPreferencesModal.module.scss'

// oxlint-disable-next-line react/only-export-components
const ViewPreferences = () => {
  const controls = useModalControls()
  const mst = useMst()
  const {
    hideCollabAvatars,
    showCollabCursors,
    hideBlockMenu,
    showMarginRuler,
  } = mst.user.prefs

  const [showAvatars, setShowAvatars] = useState(!(hideCollabAvatars ?? false))
  const [showCursors, setShowCursors] = useState(showCollabCursors ?? false)
  const [showMenu, setShowMenu] = useState(!(hideBlockMenu ?? false))
  const [showRuler, setShowRuler] = useState(showMarginRuler ?? false)

  const togglePrefs = () => {
    mst.user.updatePreferences({
      hideCollabAvatars: !showAvatars,
      showCollabCursors: showCursors,
      hideBlockMenu: !showMenu,
      showMarginRuler: showRuler,
    })
    mst.currentScript?.pmEditor.rerender()
    controls.onClose()
  }

  return (
    <ModalShell
      title="View preferences"
      onConfirm={togglePrefs}
      onClose={controls.onClose}
      confirmLabel="Save"
      cancelLabel="Cancel"
      size="md"
      opened={controls.opened}
    >
      <Preference
        label="Show collaborators in margin"
        description="You'll see and be able to jump to other people's avatars on the left of the script"
        checked={showAvatars}
        onChange={setShowAvatars}
        image={{ src: avatars, alt: 'collaborator avatars' }}
      />
      <Preference
        label="Show collaborator cursors"
        description="You'll see other people's cursors and text selections"
        checked={showCursors}
        onChange={setShowCursors}
        image={{ src: cursors, alt: 'collaborator cursors' }}
      />
      <Preference
        label="Show inline block type menu"
        description="You'll see the current block type represented by an icon in the left margin"
        checked={showMenu}
        onChange={setShowMenu}
        disabled={mst.currentScript?.isInk}
        image={{ src: blockmenu, alt: 'block menu' }}
      />
      <Preference
        label="Show ruler"
        description="You'll see a ruler that you can use to adjust individual block margins"
        checked={showRuler}
        onChange={setShowRuler}
        disabled={mst.user.prefs.pageless}
        image={{ src: ruler, alt: 'ruler' }}
      />
    </ModalShell>
  )
}

interface PreferenceProps {
  label: string
  description: string
  checked: boolean
  onChange(value: boolean): void
  disabled?: boolean
  image?: {
    src: string
    alt: string
  }
}

// oxlint-disable-next-line react/only-export-components
const Preference = (props: PreferenceProps) => {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Checkbox
        classNames={{
          body: styles.collabPreferenceModal_body,
          description: styles.collabPreferenceModal_description,
        }}
        label={<Text fw="bold">{props.label}</Text>}
        description={props.description}
        checked={props.checked}
        onChange={() => props.onChange(!props.checked)}
        disabled={props.disabled}
      />
      {props.image && <img src={props.image.src} alt={props.image.alt} />}
    </Group>
  )
}

const ViewPreferenceModal = NiceModal.create(ViewPreferences)

// See (../../Modals/readme.md)
export const showViewPreferencesModal = () =>
  NiceModal.show(ViewPreferenceModal)
